import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/types';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
  className?: string;
}

export default function ArticleCard({ article, variant = 'default', className = '' }: ArticleCardProps) {
  const formattedDate = new Date(article.publishedAt).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className={`group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors ${className}`}
      >
        <div className="w-24 h-16 flex-shrink-0 overflow-hidden rounded-md">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            title={`${article.title} - 17吃瓜`}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h3>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-500">
            <span>{formattedDate}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-xs"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className={`group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-colors ${className}`}
      >
        <div className="relative w-full h-56 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            title={`${article.title} - 17吃瓜`}
          />
          {article.isHot && (
            <span className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
              <i className="ri-fire-line text-xs"></i>
              热门
            </span>
          )}
        </div>
        <div className="p-5">
          <h3 className="text-lg font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
          <div className="flex items-center gap-4 mt-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {formattedDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {article.comments}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className={`group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all ${className}`}
    >
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={`${article.title} - 17吃瓜`}
        />
        {article.isHot && (
          <span className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
            <i className="ri-fire-line text-xs"></i>
            热门
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="inline-block text-xs font-medium text-primary-500 bg-primary-100 px-2 py-1 rounded-full mb-3">
          {article.category === 'mingxing-bagua' ? '明星八卦' :
           article.category === 'zongyi-jieMu' ? '综艺节目' :
           article.category === 'yingshi-zixun' ? '影视资讯' :
           article.category === 'wanghong-redian' ? '网红热点' : '社会热点'}
        </span>
        <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
        <div className="flex items-center gap-4 mt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-time-line"></i>
            {formattedDate}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i>
            {article.comments}
          </span>
        </div>
      </div>
    </Link>
  );
}